<template>
  <div>
    <div class="d-flex align-items-center">
      <h1 class="h3">
        {{ $t('po.title') }}
      </h1>
      <div class="ml-auto">
        <a-button type="primary" size="large" @click="toAddPurchaseOrder()">
          <a-icon type="plus-square" />
          {{ $t('po.create') }}
        </a-button>
      </div>
    </div>

    <a-row class="my-3" type="flex" justify="space-between" align="middle">
      <a-col :span="8" class="d-flex align-items-center">
        <a-input-search
          :value="params.query"
          :placeholder="$t('po.search_placeholder')"
          allow-clear
          @change="onChangeSearch"
          @search="onSearchPO"
        />
      </a-col>

      <a-col :span="10" class="d-flex align-items-center justify-content-end">
        <label class="mr-2 mb-0" style="white-space: nowrap">{{ $t('order.sortBy') }}</label>
        <a-select
          :value="params.sort"
          :placeholder="$t('utils.sortBy')"
          style="width: 250px"
          allow-clear
          @select="handleSelect"
        >
          <a-select-option
            v-for="(item) in sorting_optional"
            :key="item.label"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-col>
    </a-row>
    <div class="mt-4">
      <a-table
        class="table-list"
        :data-source="purchaseOrders"
        :row-key="record => record.id"
        :columns="columns"
        :loading="isLoading"
        :pagination="false"
        :scroll="scroll"
      >
        <template slot="supplierDO" slot-scope="text">
          {{ text || '-' }}
        </template>
        <template slot="PODate" slot-scope="text">
          {{ text ? $moment(text).format('DD MMM YYYY') : '-' }}
        </template>
        <template slot="requestDeliveryDate" slot-scope="text">
          {{ text ? $moment(text).format('DD MMM YYYY') : '-' }}
        </template>
        <template slot="status" slot-scope="text">
          <a-tag v-if="text" :color="stateColorTag[text]">
            {{ $t(`po.${text?.toLowerCase()}`) }}
          </a-tag>
          <span v-else> - </span>
        </template>
        <template slot="warehouseId" slot-scope="text">
          {{ findWarehouse(text, 'name') }}
        </template>
        <template slot="user_name" slot-scope="text">
          {{ text || '-' }}
        </template>
        <template slot="createdAt" slot-scope="text">
          {{ text ? $moment(text).format('DD MMM YYYY') : '-' }}
        </template>
        <template slot="action" slot-scope="text, record">
          <a-dropdown>
            <a @click.prevent="">
              <a-icon type="more" />
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item key="1" class="py-2" @click="onShowModalDetailPO(record)">
                  <a-icon type="eye" />
                  {{ $t('store_list_page.view_detail') }}
                </a-menu-item>
                <a-menu-item
                  v-if="canShowCreateGR(record)"
                  key="2"
                  class="py-2"
                  @click="onCreateGR(record)"
                >
                  <a-icon type="file-add" />
                  {{ $t('po.create_gr') }}
                </a-menu-item>
                <a-menu-item
                  v-if="isSuperAdmin && record.wms_code === 'javelin' && record.status.toUpperCase() === 'CREATED'"
                  key="3"
                  class="py-2"
                  @click="onRepushInbound(record)"
                >
                  <a-icon type="container" />
                  {{ $t('po.repush_to_javelin') }}
                </a-menu-item>
                <a-menu-item v-if="isEligibleToCancel(record)" key="4" class="py-2 text-danger" @click="onConfirmDelete(record)">
                  <a-icon type="delete" />
                  {{ $t('po.cancel') }}
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </template>
      </a-table>
    </div>
    <div class="mt-4 text-right">
      <a-pagination
        v-model="params.page"
        :page-size="params.size"
        :page-size-options="sizeOptions"
        :total="totalRow"
        :show-total="(total, range) => $t('utils.pagination_show_total', { perpage: `${range[0]} - ${range[1]}`, total })"
        :locale="{ items_per_page: $t('utils.items_per_page') }"
        :disabled="isLoading"
        show-size-changer
        @change="onPageChange"
        @showSizeChange="onShowSizeChange"
      />
    </div>

    <ModalDetailPO
      :data="detailDataPO"
      :is-loading="isLoading"
      :visible="showModalDetailPO"
      :business-id="businessId"
      @onCancel="showModalDetailPO = false, detailDataPO = {}"
    />
  </div>
</template>

<script>
export default {
  name: 'ListPurchaseOrder',
}
</script>
<script setup>
import debounce from 'lodash/debounce'
import { computed, getCurrentInstance, onMounted, reactive, ref, toRefs, watch } from 'vue'
import { getListPO, deletePO, repushInboundDelivery } from '@/api/purchase-order'
import ModalDetailPO from '@/views/purchase-order/modal/ModalDetailPO.vue'
import { Modal } from 'ant-design-vue';

const { $i18n, $route, $router, $store, $moment, $notification } = getCurrentInstance().proxy.$root

const INITIAL_SORT = {
  sort: ['createdAt'],
  order: ['DESC'],
}

const STATE_COLOR_TAG = {
  CREATED: 'blue',
  PARTIAL_RECEIVED: 'orange',
  FULL_RECEIVED: 'green',
  CANCELED: 'red',
}
const INITIAL_PARAMS = {
  query: '',
  start_date: '',
  end_date: '',
  sort: 'createdAt,desc',
  page: 1,
  size: 10,
}

// Data
const screenHeight = ref('calc(100vh - 300px)')
const isLoading = ref(false)
const showModalDetailPO = ref(false)
const purchaseOrders = ref([])
const warehouseList = ref([])
const totalRow = ref(0)
const selectedDiscounts = ref([])
const detailDataPO = ref({})
const sizeOptions = ref(['10', '20', '30', '40', '50'])
const sorting = reactive(INITIAL_SORT)
const params = reactive(INITIAL_PARAMS)
const stateColorTag = ref(STATE_COLOR_TAG)



// Refs
const refSorting = toRefs(sorting)
const refParams = toRefs(params)

// Computed
const businessId = computed(() => {
  return $store.state.user.restriction_base === 'Warehouse'
    ? Object.keys($store.getters['user/businessListObjectByKey'])[0]
    : $route.query.business_id
})
const columns = computed(() => [
  {
    title: $i18n.t('po.columns.number'),
    dataIndex: 'number',
    key: 'number',
    width: 200,
    scopedSlots: {
      customRender: 'numberPO',
    },
  },
  {
    title: $i18n.t('po.columns.po_date'),
    dataIndex: 'po_date',
    key: 'po_date',
    width: 120,
    scopedSlots: {
      customRender: 'PODate',
    },
  },
  {
    title: $i18n.t('po.columns.supplier_do'),
    dataIndex: 'supplier_do',
    key: 'supplier_do',
    width: 190,
    scopedSlots: {
      customRender: 'supplierDO',
    },
  },
  {
    title: $i18n.t('po.columns.delivery_date'),
    dataIndex: 'request_delivery_date',
    key: 'request_delivery_date',
    width: 120,
    scopedSlots: {
      customRender: 'requestDeliveryDate',
    },
  },
  {
    title: $i18n.t('po.columns.supplier'),
    dataIndex: 'supplier_code',
    key: 'supplier_code',
    width: 150,
    scopedSlots: {
      customRender: 'supplier',
    },
  },
  {
    title: $i18n.t('po.columns.warehouse'),
    dataIndex: 'warehouse_id',
    key: 'warehouse_id',
    width: 150,
    scopedSlots: {
      customRender: 'warehouseId',
    },
  },
  {
    title: $i18n.t('po.columns.created_by'),
    dataIndex: 'user_name',
    key: 'user_name',
    width: 150,
    scopedSlots: {
      customRender: 'user_name',
    },
  },
  {
    title: $i18n.t('po.columns.created_at'),
    dataIndex: 'created_at',
    key: 'created_at',
    width: 120,
    scopedSlots: {
      customRender: 'createdAt',
    },
  },
  {
    title: $i18n.t('po.columns.status'),
    dataIndex: 'status',
    key: 'status',
    width: 150,
    scopedSlots: {
      customRender: 'status',
    },
  },
  {
    title: '',
    key: 'action',
    dataIndex: 'action',
    width: 50,
    scopedSlots: { customRender: 'action' },
  },
])
const sorting_optional = computed(() => [
  {
    label: $i18n.t('po.sort_po_date_newest'),
    value: 'poDate,desc',
  },
  {
    label: $i18n.t('po.sort_po_date_oldest'),
    value: 'poDate,asc',
  },
  {
    label: $i18n.t('po.sort_created_at_newest'),
    value: 'createdAt,desc',
  },
  {
    label: $i18n.t('po.sort_created_at_oldest'),
    value: 'createdAt,asc',
  },
  {
    label: $i18n.t('po.sort_number_asc'),
    value: 'number,asc',
  },
  {
    label: $i18n.t('po.sort_number_desc'),
    value: 'number,desc',
  },
  {
    label: $i18n.t('po.sort_supplier_asc'),
    value: 'supplierCode,asc',
  },
  {
    label: $i18n.t('po.sort_supplier_desc'),
    value: 'supplierCode,desc',
  },
])
const scroll = computed(() => ({ x: '1000px' }))
const tHeight = computed(() => {
  return !purchaseOrders.value.length ? '0px' : 'calc(100vh - 300px)'
})
const isSuperAdmin = computed(() => {
  return ["1a5e240b-535d-45c7-9646-b19b2047daa2", "30d9817c-07ef-45b8-a36c-319f86734b29", 'b7575f5b-1b45-4f53-b3fa-fccc9f7f1a42'].includes($store.state.user.id)
})

// Methods
const toAddPurchaseOrder = () => {
  const query = {
    ...$route.query,
  }
  $router.push({
    path: `/purchase-order/create`,
    query,
  })
}

const handleSelect = (e) => {
  params.sort = e
  fetchListPO()
}

const init = () => {
  selectedDiscounts.value = []
  refSorting.value = INITIAL_SORT
  refParams.value = INITIAL_PARAMS
  Promise.all([fetchWarehouseList(), fetchListPO()])
}

const fetchListPO = debounce(async () => {
  isLoading.value = true
  const selectedBusinessId = $route.query.business_id || businessId.value
  getListPO({
    business_id: selectedBusinessId,
    params: {
      ...params,
      business_id: selectedBusinessId,
    },
  })
  .then(({ data }) => {
    purchaseOrders.value = warehouseList.value && data?.data
      ? data.data.map(item => {
          const findWarehouse = warehouseList.value.find(warehouse => warehouse.id === item.warehouse_id)
          return {
            ...item,
            wms_code: findWarehouse?.wms_code,
          }
        })
      : []
    totalRow.value = warehouseList.value && data?.data
      ? data?.total_items
      : 0
  })
  .catch(() => {
    purchaseOrders.value = []
    totalRow.value = 0
  })
  .finally(() => isLoading.value = false)
}, 500)

const onPageChange = (pageNumber) => {
  selectedDiscounts.value = []
  params.page = pageNumber
  fetchListPO()
}

const onShowSizeChange = (current, pageSize) => {
  selectedDiscounts.value = []
  params.page = current
  params.size = pageSize
  fetchListPO()
}

const onChangeSearch = (e) => {
  params.query = e?.target?.value || ''
  if (!params.query) {
    params.page = 1
    fetchListPO()
  }
}
const onSearchPO = () => {
  params.page = 1
  fetchListPO()
}

const fetchWarehouseList = () => {
  const selectedBusinessId = $route.query.business_id || businessId.value
  $store.dispatch('warehouse/GETWAREHOUSELIST', {
      ...$route.query,
      business_id: selectedBusinessId,
      page: 1,
      size: 150,
    })
    .then(response => {
      warehouseList.value = response
    })
    .catch(err => {
      console.error(err)
      return err
    })
}
const findWarehouse = (id, field) => {
  const warehouse = warehouseList.value.find((item) => item.id === id)
  return warehouse ? warehouse[field] : '-'
}

const onShowModalDetailPO = (record) => {
  detailDataPO.value = {
    ...record,
    whs_name: findWarehouse(record.warehouse_id, 'name'),
  }
  showModalDetailPO.value = true
}
const onConfirmDelete = (record) => {
  Modal.confirm({
    content: $i18n.t('po.cancel_description'),
    okText: $i18n.t('po.cancel_confirm'),
    cancelText: $i18n.t('utils.close'),
    onOk: () => {
      const { business_id, id, number } = record
      const payload = {
        po_id: id,
        po_number: number,
        business_id,
      }
      return deletePO({
        business_id,
        data: payload,
      })
      .then(({ data }) => {
        $notification.success({
          message: $i18n.t('utils.success'),
          description: `PO ${data.data.poNumber ? 'dengan no. ' + data.data.poNumber : ''} ` + $i18n.t('po.cancel_success'),
        })
        fetchListPO()
      })
      .catch(err => {
        console.error(err)
        $notification.error({
          message: $i18n.t('utils.failed'),
          description: err.response?.data?.data || $i18n.t('po.cancel_failed') || 'Terjadi kesalahan saat membatalkan PO',
        })
      })
    },
  });
};
const canShowCreateGR = (record) => {
  const warehouseId = record?.warehouse_id
  const warehouse = warehouseList.value.find((item) => item.id === warehouseId)
  const wmsCode = warehouse?.wms_code
  const flagWms = !(wmsCode === null || (typeof wmsCode === 'string' && (wmsCode.toUpperCase() === 'N/A' || wmsCode === '')))

  const status = record?.status ? String(record.status).toUpperCase() : ''
  const notAllowedStatus = ['FULL_RECEIVED', 'CANCELED']

  return !flagWms && !notAllowedStatus.includes(status)
}

const isEligibleToCancel = (record) => {
  const status = record?.status ? String(record.status).toUpperCase() : ''
  const allowCancel = ['CREATED']

  return allowCancel.includes(status)
}

const onCreateGR = (record) => {
  const warehouseId = record?.warehouse_id
  const poId = record?.id
  $router.push({
    name: 'inventory.goods.receipt.create',
    query: {
      ...$route.query,
      warehouseId,
      ...(poId ? { po_id: poId } : {}),
    },
  })
}

const onRepushInbound = debounce((record) => {
  isLoading.value = true
  repushInboundDelivery({
    po_ids: [record.id],
    wms_code: record.wms_code,
  })
  .then(() => $notification.success({ message: $i18n.t('po.repush_to_javelin_success') }))
  .catch((err) => {
    $notification.error({
      message: $i18n.t('po.repush_to_javelin_failed'),
      description: err.response?.data?.message || err.message,
    })
  })
  .finally(() => isLoading.value = false)
}, 500)

// Watch
watch(() => 
  businessId.value, (newValue) => {
    businessId.value = newValue
    init()
  })
watch(() => 
  $route, (newValue) => {
    if (newValue.name === $route.name) {
      init()
    }
  })

// Mounted
onMounted(init)
</script>

<style lang="scss">
.fs-14 {
  font-size: 14px !important;
}
.fw-bold {
  font-weight: bold;
}
.text-capitalize {
  text-transform: capitalize
}
.sorting-checkbox {
  .ant-checkbox-group-item {
    display: block !important;
    padding: 5px 0;
    margin-bottom: 5px;

    &:last-child {
      margin-bottom: 0;
    }
  }
  .ant-checkbox-inner {
    border-color: transparent !important;
  }
  .ant-checkbox-checked {
    &::after {
      border-color: transparent !important;
    }
    .ant-checkbox-inner {
      background-color: transparent !important;

      &::after {
        border-color: #ff0a54 !important;
      }
    }
  }
}
.dropdown-title {
  font-size: 12px;
  font-weight: bold;
  color: #6c757d;
}
.table-list .ant-table-body {
  max-height: calc(100vh - 300px) !important;
  min-height: auto !important;
  overflow-y: auto !important;
}
.ant-btn-info {
  color: #2196f3 !important;
  border-color: #2196f3 !important;
}
.ant-dropdown-menu-item {
  background: #fff !important;
}
.ant-table-row {
  background-color: #fff !important;

  &.ant-table-row-selected > td {
    background-color: #fff !important;
  }
}
.pca-tab-store {
  .ant-tabs-bar {
    background-color: #fff;
    margin-bottom: 0;
    border-bottom-color: transparent;
  }
  .ant-tabs-nav .ant-tabs-tab:hover,
  .ant-tabs-nav .ant-tabs-tab-active {
    color: #ff0a54;
  }
  .ant-tabs-ink-bar {
    background-color: #ff0a54;
  }
}
.select-antd-default.ant-select-lg .ant-select-selection__rendered {
  line-height: 38px !important;
}
</style>